import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import ClientContext from 'contexts/ClientContext'
import classNames from 'classnames'
import dayjs from 'dayjs'
import PageTitle from 'components/common/PageTitle/PageTitle'
import PageSubtitle from 'components/common/PageSubtitle/PageSubtitle'
import style from './ClaimItem.module.scss'
import Arrow from 'components/assets/icons/Arrow'
import DocumentText from 'components/assets/icons/DocumentText'
import Card from 'components/common/Card/Card'
import Button from 'components/common/Button/Button'
import Clock from 'components/assets/icons/Clock'
import Status from 'components/common/Status/Status'
import CardLine from 'components/common/CardLine/CardLine'
import Thundercloud from 'components/assets/icons/Thundercloud'
import Collapsible from 'components/common/Collapsible/Collapsible'
import CircleOk from 'components/assets/icons/CircleOk'
import api from 'helpers/api'
import Download from 'components/assets/icons/Download'
import Eye from 'components/assets/icons/Eye'
import { downloadDocument, getFileUrl } from 'helpers/files'
import Link from 'components/common/Link/Link'
import ExclamationTriangle from 'components/assets/icons/ExclamationTriangle'
import { CLAIM_STATUSES, PROOF_STATUSES } from 'constants/contract'
import InfoMessage from 'components/common/InfoMessage/InfoMessage'

const ClaimItem = () => {
  const { t, i18n: { language } } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inputRef = useRef([])
  const [claim, setClaim] = useState(null)
  const [ isHover, setIsHover ] = useState(false)
  const [areCommentsOpened, setAreCommentsOpened] = useState(false)
  const [error, setError] = useState(null)
  const [tokenExpired, setTokenExpired] = useState(false)
  // Vérification IA d'un justificatif avant envoi — { [proofOfClaimsId]: { status: 'checking'|'rejected'|'forced', reason, file } }
  const [docVerdicts, setDocVerdicts] = useState({})
  const isVideoSelected = !!claim?.document?.find( doc => doc.isVideo)
  const clientDocuments = claim?.documents.sort( (a, b) => a.createdAt < b.createdAt ? 1 : -1)
  const { APPROVED, INCOMPLETE, CLOSED } = CLAIM_STATUSES
  const { ACCEPTED, CHECKING, REFUSED } = PROOF_STATUSES
  const { color, colabelNeutralDark } = useContext(ClientContext)

  // Accès temporaire via lien WhatsApp (sans login) : token dans l'URL → client axios
  // dédié, jamais le client `api` partagé (lié à la session normale, redirige au 401).
  const accessTokenParam = searchParams.get('token')
  const claimAccessClient = useMemo(
    () => accessTokenParam
      ? axios.create({
          baseURL: config.api.url,
          headers: {
            Authorization: `Bearer ${accessTokenParam}`,
            ...(config.api.xApiKey ? { 'x-api-key': config.api.xApiKey } : {}),
          },
        })
      : null,
    [accessTokenParam],
  )

  useEffect(() => {
    const getClaim = async () => {
      try {
        if (claimAccessClient) {
          const res = await claimAccessClient.get('/claim-access')
          setClaim(res.data?.response || res.data)
        } else {
          const res = await api.get({ url: `/claims/${params.id}` })
          setClaim(res)
        }
      } catch (error) {
        if (claimAccessClient) setTokenExpired(true)
        else navigate('/claims')
      }
    }
    getClaim()
  }, [params.id, navigate, language, claimAccessClient])


  const getDocumentUrl = useCallback(
    async document => {
      const url = await getFileUrl({
        claimId: claim._id,
        contractId: claim.adhesionId,
        documentId: document._id
      })
      window.open(url, '_blank').focus()
    },
    [claim],
  )


  const displayError = (msg) => {
    window.scrollTo(0, 0)
    setError(msg)
    setTimeout(() => setError(null), 5000)
  }

  // Vérification IA du fichier avant tout envoi — aucune persistance, juste un verdict.
  const validateDocumentPreview = async (file, proofOfClaimsId) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('proofOfClaimsId', proofOfClaimsId)

    return claimAccessClient
      ? (await claimAccessClient.post('/claim-access/documents/validate-preview', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })).data.response
      : (await api.post({
          url: '/claims/documents/validate-preview',
          contentType: 'multipart/form-data',
          data: formData,
        })).response
  }

  const uploadDocument = async (file, proofOfClaimsId) => {
    try {
      const formData = new FormData()
      const mixedName = JSON.stringify({ name: file.name, proofOfClaimsId })
      formData.append('files', file, mixedName)

      const { documents, status } = claimAccessClient
        ? (await claimAccessClient.post('/claim-access/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })).data.response.claim
        : (await api.post({
            url: `/claims/${claim._id}/documents`,
            contentType: 'multipart/form-data',
            data: formData,
          })).response.claim
      setClaim({ ...claim, documents, status })
    } catch (error) {
      displayError(t('common:form.genericError'))
    }
  }

  const onFilesSubmit = async (event, index) => {
    let file = null
    const item = event.target.files[0]
    const proofOfClaimsId = claim.nature.proofOfClaims[index]._id

    if (item.size > process.env.REACT_APP_FILESIZE_LIMIT){
      displayError(t('claim:item.fileTooLarge'))
    } else if (item.type.startsWith('video/') && isVideoSelected){
      displayError(t('claim:item.limitVideoQuantity'))
    } else if (!item.type.match(/image|pdf|video/i)) {
      displayError(t('claim:item.fileFormatForbidden'))
    } else {
      item.proofOfClaimsId = proofOfClaimsId
      file = item
    }

    if (!file) return

    setDocVerdicts(prev => ({ ...prev, [proofOfClaimsId]: { status: 'checking' } }))

    let verdict = null
    try {
      verdict = await validateDocumentPreview(file, proofOfClaimsId)
    } catch (e) {
      verdict = null // panne réseau/IA → on n'empêche jamais l'envoi, on accepte
    }

    if (verdict?.checked && verdict.matches === false) {
      setDocVerdicts(prev => ({ ...prev, [proofOfClaimsId]: { status: 'rejected', reason: verdict.reason, file } }))
      return
    }

    setDocVerdicts(prev => {
      const next = { ...prev }
      delete next[proofOfClaimsId]
      return next
    })
    await uploadDocument(file, proofOfClaimsId)
  }

  const handleForceAccept = async proofOfClaimsId => {
    const pending = docVerdicts[proofOfClaimsId]
    if (!pending?.file) return
    setDocVerdicts(prev => ({ ...prev, [proofOfClaimsId]: { status: 'forced', reason: pending.reason } }))
    await uploadDocument(pending.file, proofOfClaimsId)
  }

  const handleUploadClick = index => {
    if (inputRef.current[index]) {
      inputRef.current[index].click()
    }
  }


  const sortedComments = useMemo(
    () => (claim?.commentsClaims || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [claim?.commentsClaims],
  )
  const [lastComment, ...comments] = sortedComments

  const Comment = ({ item }) => {
    return (
      <div className={style.comment}>
        <CardLine
          className="capitalized"
          title={dayjs(item.createdAt).locale('fr').format('DD.MM.YYYY')}
          styles={{ marginBottom: 0 }}
        />
        <CardLine content={item.commentsDashboard} styles={{ marginBottom: 0 }} />
      </div>
    )
  }


  const StatusBlock = () => (
    <ClaimCard
      icon={
        claim.status === APPROVED
        ? <CircleOk />
        : [CLOSED, INCOMPLETE].includes(claim.status)
          ? <ExclamationTriangle />
          : <Clock />
      }
      content={
        <>
          <p className={classNames(style['last-comment'], style.light)}>
            {lastComment
              ? lastComment.commentsDashboard
              : claim.status === INCOMPLETE
                ? t('claim:item.missingDocuments')
                : t('claim:item.defaultComment')
            }
          </p>

          {!!comments.length && (
            <>
              <div className={style.comments}>
                {areCommentsOpened && comments.map((item, index) => (
                  <Comment item={item} key={`claim-comment-${index}`} />
                ))}
              </div>
              <Link
                className={style['comments-link']}
                label={`${areCommentsOpened ? t('common:hide') : t('common:see')} ${t('common:history')}`}
                onClick={() => setAreCommentsOpened(!areCommentsOpened)}
              />
            </>
          )}
        </>
      }
    />
  )


  const ClaimCard = ({ content, icon }) => {
    const { status } = claim
    const type = status === APPROVED ? 'success' : status === INCOMPLETE || status === CLOSED ? 'error' : 'warning'
    const updatedAt = lastComment?.createdAt || claim.updatedAt
    const statusDate = dayjs(updatedAt).locale('it').format('DD.MM.YYYY');

    return (
      <Card
        className={classNames(style.card, style.status)}
        content={content}
        title={<span className={classNames(style['status-date'], 'capitalized')}>{statusDate}</span>}
        titleIcon={icon}
        cornerContent={<Status label={t(`claim:item.status.${status}`)} type={type} />}
        type={type}
      />
    )
  }


  // Required documents status

  const DocAcceped = ({file}) => (
    <div>
      <Link
        className={style['document-link']}
        label={<Download />}
        onClick={() => downloadDocument({
          claim,
          contract: { _id: claim.adhesionId },
          documentItem: {...file, name: file.originalName || file.name}
        })}
      />
      <Link
        className={style['document-link']}
        label={<Eye className={style.eye} />}
        onClick={() => getDocumentUrl(file)}
        target="_blank"
        rel="noopener noreferrer"
      />
    </div>
  )

  const CheckingDoc = () => (
    <div>
      <Clock />{t(`claim:item.status.checking`)}
    </div>
  )



  if (tokenExpired) {
    return (
      <Card
        className={style.card}
        content="Ce lien a expiré ou n'est plus valide. Merci de vous reconnecter à votre espace client Owen, ou de recontacter l'assistant pour recevoir un nouveau lien."
        title="Lien expiré"
      />
    )
  }

  return claim && (
    <>
      <PageTitle text={t('claim:item.title')} />
      <PageSubtitle text={t('claim:item.subtitle')} />

      {error && <InfoMessage
        className={style.infoMessage}
        type='error'
        label={error}
        prefix={<ExclamationTriangle />}
      />}

      <StatusBlock />
      <Card
        className={style.card}
        content={claim.description}
        onClick={() => navigate(`/contracts/${claim.adhesionId}`)}
        title={claim.name}
        titleIcon={<DocumentText styles={color && {fill: colabelNeutralDark}} />}
        cornerContent={<Arrow styles={color && {fill: colabelNeutralDark}} />}
      />

      <Collapsible
        title={
          <>
            <Thundercloud styles={{ fill: color ? colabelNeutralDark : '#1414FF' }} />
            <div className={style['collapsible-title']}>#{claim.shortId}</div>
          </>
        }
        isOpenedProp={true}
        content={
          <>
            <div className={style['summary-first-line']}>
              <CardLine
                className={style['first-line-element']}
                title={t('claim:item.date')}
                content={dayjs(claim.createdAt).format('DD/MM/YYYY')}
              />
              <CardLine
                className={style['first-line-element']}
                title={t('claim:item.location')}
                content={claim.location}
              />
              <CardLine
                className={style['first-line-element']}
                title={t('claim:item.nature')}
                content={claim.nature?.name || t('common:other')}
              />
            </div>
            <CardLine
              className={style.circumstances}
              title={t('claim:item.circumstances')}
              content={claim.circumstance}
            />

            {!!(claim.nature && claim.nature.proofOfClaims?.length > 0) &&
              <CardLine
                className={style['required-documents']}
                styles={color && {color: colabelNeutralDark}}
                title={<>
                  {t('claim:applySteps.documents.requiredDocuments')}
                  <br/>
                  {t('claim:applySteps.documents.contact')}
                </>}
                content={
                  <ul>
                    {claim.nature.proofOfClaims.map( (proof, index) => {
                      const file = clientDocuments.find( elm => elm.proofOfClaimsId === proof._id )
                      const checking = file?.status === CHECKING
                      const accepted = file?.status === ACCEPTED
                      const rejected = file?.status === REFUSED
                      const verdict = !file ? docVerdicts[proof._id] : null
                      const aiChecking = verdict?.status === 'checking'
                      const aiRejected = verdict?.status === 'rejected'
                      const aiForced = verdict?.status === 'forced'
                      return (
                        <li
                          className={classNames(
                            rejected ? style.rejected : checking ? style.checking : '',
                            aiRejected && style.aiRejected,
                            aiForced && style.aiForced,
                          )}
                          key={`document-${index}`}
                        >
                          <legend>
                            {(!file || rejected) && <ExclamationTriangle />}
                            {proof.name}
                          </legend>
                          { checking && <CheckingDoc /> }
                          { accepted && <DocAcceped file={file} /> }
                          { aiChecking && <span className={style.aiChecking}>Vérification...</span> }
                          {(!file || rejected) && !aiChecking &&
                            <div
                              style={{
                                cursor:'pointer',
                                color: color && (isHover === index ? color : colabelNeutralDark)
                              }}
                              onClick={() => handleUploadClick(index)}
                              onMouseEnter={() => setIsHover(index)}
                              onMouseLeave={() => setIsHover(null)}
                            >
                              <Download styles={color && {fill: (isHover === index ? color : colabelNeutralDark)}} />
                              {t('common:upload')}
                              <input
                                type="file"
                                accept={'image/*, .pdf' + (!isVideoSelected ? ', video/*' : '')}
                                ref={el => (inputRef.current[index] = el)}
                                onChange={e => onFilesSubmit(e, index)}
                                hidden
                              />
                            </div>
                          }
                          { aiRejected &&
                            <div className={style.aiVerdict}>
                              <p>⚠️ Ce document ne ressemble pas au justificatif attendu{verdict.reason ? ` (${verdict.reason})` : ''}. Vérifiez le fichier et téléversez-en un autre.</p>
                              <button
                                type="button"
                                className={style.overrideBtn}
                                onClick={() => handleForceAccept(proof._id)}
                              >
                                C'est le bon document → transmettre à un agent
                              </button>
                            </div>
                          }
                        </li>
                      )
                    })}
                  </ul>
                }
              />
            }
          </>
        }
      />

      {!claimAccessClient &&
        <Button
          className={style.button}
          label={t('claim:item.myClaimsButton')}
          onClick={() => navigate('/claims')}
          type="secondary"
        />
      }
    </>
  )
}

export default ClaimItem
